import React from 'react';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import AuthService from '../../../Services/authService';
import Loading from '../../../Elements/loading';
import FaIcon from '../../../Elements/FaIcon';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import EmployeeDayPlanItem from '../../../Domain/EmployeeDayPlanItem';
import AvailabilityPlannerPanelDescription from './AvailabilityPlannerPanelDescription';
import AvailabilityPlannerTable from './AvailabilityPlannerTable';
import AvailabilityPlannerTeamGrid from './AvailabilityPlannerTeamGrid';
import AvailabilityPlannerStats from './AvailabilityPlannerStats';
import AvailabilityPlannerCoverage from './AvailabilityPlannerCoverage';
import { LOCATION, ABSENCE } from './availabilityTaxonomy';

export default class AvailabilityPlannerAdmin extends React.Component {
  constructor(props) {
    super(props);
    const authService = AuthService.getInstance();
    this.state = {
      baseUser: authService.GetLoggedInUser(),
      users: [],
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      currentUserId: 0,
      loading: true,
      // Background data refresh (after an edit / month change): the table stays visible, slightly dimmed,
      // with a small spinner beside the month name — no full-page loader after the initial load.
      refreshing: false,
      window: null,
      view: 'team',
      // Right-click quick menu on a day cell: {x, y, user, dayNumber, hasEntry} while open, else null.
      contextMenu: null,
    };
    this.closeContextMenu = this.closeContextMenu.bind(this);
    this.onContextMenuKeyDown = this.onContextMenuKeyDown.bind(this);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onContextMenuKeyDown);
  }

  onContextMenuKeyDown(e) {
    if (e.key === 'Escape') {
      this.closeContextMenu();
    }
  }

  openDayContextMenu(e, user, dayNumber, hasEntry) {
    e.preventDefault();
    // Clamp so the menu stays inside the viewport (approx. menu size 200x360).
    const x = Math.min(e.clientX, window.innerWidth - 210);
    const y = Math.min(e.clientY, window.innerHeight - 370);
    document.addEventListener('keydown', this.onContextMenuKeyDown);
    this.setState({ contextMenu: { x, y, user, dayNumber, hasEntry } });
  }

  closeContextMenu() {
    document.removeEventListener('keydown', this.onContextMenuKeyDown);
    this.setState({ contextMenu: null });
  }

  openDayPanel(user, startDay, endDay) {
    // Editing is limited to the caller and their direct reports (the backend enforces the same).
    if (!user.canEdit) {
      return;
    }
    const start = Math.min(startDay, endDay);
    const end = Math.max(startDay, endDay);
    const startDate = new Date(this.state.year, this.state.month - 1, start);
    const endDate = new Date(this.state.year, this.state.month - 1, end);
    const dayString = dateTimeHelper.toYYYYsMMsDD(startDate, '-');
    const hasPlanOn = (dayNum) => {
      const ds = dateTimeHelper.toYYYYsMMsDD(new Date(this.state.year, this.state.month - 1, dayNum), '-');
      return (user.dayPlans || []).some((p) => p.day && p.day.substring(0, 10) === ds);
    };
    // Prefill from an existing entry only for a single day; a multi-day range is always a fresh create.
    const plan =
      start === end ? (user.dayPlans || []).find((p) => p.day && p.day.substring(0, 10) === dayString) : null;
    // The range already holds entries if any day in it does — so a multi-day selection over existing entries
    // opens as an edit (and shows Delete), not as a blank create.
    let rangeHasEntry = false;
    for (let d = start; d <= end && !rangeHasEntry; d++) {
      rangeHasEntry = hasPlanOn(d);
    }

    const item = new EmployeeDayPlanItem();
    item.TargetUserId = user.userId;
    item.StartDate = startDate;
    item.EndDate = endDate;
    // The user's default work location, used to reset the location when an absence is selected; the
    // standard daily hours prefill a full absence day when an absence type is picked.
    item.DefaultLocation = user.defaultWorkLocation || 'office';
    item.StandardDailyHours = user.standardDailyHours || 8;
    if (plan) {
      item.Location = plan.location || '';
      item.WorkingHours = plan.workingHours;
      item.AbsenceType = plan.absenceType || '';
      item.AbsenceHours = plan.absenceHours;
      // Carry the real status; an approved absence is locked in the panel (work stays editable).
      item.Status = plan.status || 'planned';
    } else {
      item.Location = user.defaultWorkLocation || 'office';
      item.WorkingHours = user.standardDailyHours || 8;
      item.AbsenceType = '';
      item.AbsenceHours = 0;
    }

    const hadEntry = rangeHasEntry;
    this.setState({
      window: (
        <GeneralEditWizzardBase
          crudType={rangeHasEntry ? CrudTypesEnum.Update : CrudTypesEnum.Create}
          editedItem={item}
          clickOnCancel={() => this.setState({ window: null })}
          clickOnSave={() => {
            this.setState({ window: null });
            this.populate(this.state.currentUserId);
          }}
          approveBeforeSaveCheck={(edited) => this.needsSaveConfirm(edited, hadEntry)}
          approveBeforeSaveMessage={(edited) => this.saveConfirmMessage(edited)}
          windowDescription={AvailabilityPlannerPanelDescription}
          errorCode='AP53'
        />
      ),
    });
  }

  // Builds a single-day item shell for the quick actions (bolt + context menu).
  singleDayItem(user, dayNumber) {
    const date = new Date(this.state.year, this.state.month - 1, dayNumber);
    const item = new EmployeeDayPlanItem();
    item.TargetUserId = user.userId;
    item.StartDate = date;
    item.EndDate = date;
    item.Status = 'planned';
    return item;
  }

  // Saves a quick-action item directly (no panel) and refreshes the month in the background — the small
  // refresh spinner covers the save round-trip too (no full-page overlay).
  saveQuickItem(item) {
    this.setState({ refreshing: true });
    AvailabilityPlannerService.SaveDay(item)
      .then((response) => {
        if (response && (response.error || response.errorMessage)) {
          this.setState({ refreshing: false });
          ErrorMessage.SetErrorMessage(response.error || response.errorMessage);
          return;
        }
        this.populate(this.state.currentUserId);
      })
      .catch(() => {
        this.setState({ refreshing: false });
        ErrorMessage.SetErrorMessage('AP54');
      });
  }

  // One-click add of a default work day (default location + standard hours), saved directly without the
  // panel. Same defaults a fresh create would prefill in openDayPanel.
  quickCreate(user, dayNumber) {
    if (!user.canEdit) {
      return;
    }
    const item = this.singleDayItem(user, dayNumber);
    item.Location = user.defaultWorkLocation || 'office';
    item.WorkingHours = user.standardDailyHours || 8;
    item.AbsenceType = '';
    item.AbsenceHours = 0;
    this.saveQuickItem(item);
  }

  // Context-menu action: set the day to a FULL work day at the given location, or a FULL absence of the
  // given type — overwriting whatever the day held (the menu is not offered on BambooHR-locked days).
  quickSetDay(user, dayNumber, kind, code) {
    this.closeContextMenu();
    if (!user.canEdit) {
      return;
    }
    const item = this.singleDayItem(user, dayNumber);
    const fullDay = user.standardDailyHours || 8;
    if (kind === 'work') {
      item.Location = code;
      item.WorkingHours = fullDay;
      item.AbsenceType = '';
      item.AbsenceHours = 0;
    } else {
      item.Location = '';
      item.WorkingHours = 0;
      item.AbsenceType = code;
      item.AbsenceHours = fullDay;
    }
    this.saveQuickItem(item);
  }

  // Context-menu action: clear the day (same semantics as the panel's Delete — zeroed hours remove the row).
  quickDeleteDay(user, dayNumber) {
    this.closeContextMenu();
    if (!user.canEdit) {
      return;
    }
    ErrorMessage.SetApprove('This will remove the availability entry for this day. Do you want to continue?', () => {
      const item = this.singleDayItem(user, dayNumber);
      item.Location = '';
      item.WorkingHours = 0;
      item.AbsenceType = '';
      item.AbsenceHours = 0;
      this.saveQuickItem(item);
    });
  }

  static totalHours(item) {
    return (Number(item.WorkingHours) || 0) + (Number(item.AbsenceHours) || 0);
  }

  // Confirm before saving when the save would remove an existing entry, or when the day totals over 8 hours.
  needsSaveConfirm(item, hadEntry) {
    const total = AvailabilityPlannerAdmin.totalHours(item);
    return (total === 0 && hadEntry) || total > 8;
  }

  saveConfirmMessage(item) {
    const total = AvailabilityPlannerAdmin.totalHours(item);
    if (total === 0) {
      return 'This will remove the entry for this day. Do you want to continue?';
    }
    return `The total hours (${total}) exceed the standard 8 hours. Do you want to save anyway?`;
  }

  componentDidMount() {
    this.populate(this.state.baseUser.userId);
  }

  populate(userId) {
    // Full-page loader only while there is nothing to show yet; afterwards refresh in the background so
    // the calendar never disappears.
    const background = this.state.users.length > 0;
    this.setState({ currentUserId: userId, loading: !background, refreshing: background });
    AvailabilityPlannerService.GetMonth(userId, this.state.year, this.state.month)
      .then((data) => this.setState({ users: Array.isArray(data) ? data : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('AP27'))
      .finally(() => this.setState({ loading: false, refreshing: false }));
  }

  changeMonth(delta) {
    let month = this.state.month + delta;
    let year = this.state.year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.setState({ month, year }, () => this.populate(this.state.currentUserId));
  }

  renderLegendItem(item) {
    return (
      <span
        key={item.label}
        className='ap-legend-item'
      >
        <span className={`ap-legend-swatch ${item.className}`}>
          <i className={`fa-solid ${item.icon}`} />
        </span>
        {item.label}
      </span>
    );
  }

  renderLegendGroup(title, items) {
    return (
      <div className='ap-legend-group-box'>
        <span className='ap-legend-group'>{title}</span>
        {Object.values(items).map((item) => this.renderLegendItem(item))}
      </div>
    );
  }

  // BambooHR-owned days carry a status badge: a lock (approved/confirmed) or a clock (requested/pending).
  renderStatusLegend() {
    return (
      <div className='ap-legend-group-box'>
        <span className='ap-legend-group'>BambooHR</span>
        <span className='ap-legend-item'>
          <i className='fa-solid fa-lock' /> Approved
        </span>
        <span className='ap-legend-item'>
          <i className='fa-solid fa-clock' /> Requested (pending)
        </span>
      </div>
    );
  }

  renderLegend() {
    return (
      <div className='ap-legend'>
        {this.renderLegendGroup('Working', LOCATION)}
        {this.renderLegendGroup('Absent', ABSENCE)}
        {this.renderStatusLegend()}
      </div>
    );
  }

  renderMonthSelector() {
    return (
      <div className='row p-1'>
        <div className='col-4' />
        <div className='col-1 text-center h6'>
          <FaIcon
            iconName='fa fa-chevron-left'
            tooltipText='Previous month'
            onClick={() => this.changeMonth(-1)}
          />
        </div>
        <div className='col-2 text-center h6'>
          {dateTimeHelper.MonthName(this.state.month - 1)} {this.state.year}
          {this.state.refreshing && <i className='fa-solid fa-spinner fa-spin ap-refresh-ico' />}
        </div>
        <div className='col-1 text-center h6'>
          <FaIcon
            iconName='fa fa-chevron-right'
            tooltipText='Next month'
            onClick={() => this.changeMonth(1)}
          />
        </div>
        <div className='col-4 text-center h6'>
          <FaIcon
            className='icon iconlink mr-2'
            iconName='fa fa-user'
            tooltipText='Back to the logged in user'
            onClick={() => this.populate(this.state.baseUser.userId)}
          />
          <FaIcon
            iconName='fa fa-print mr-2'
            tooltipText='Print'
            onClick={() => window.print()}
          />
        </div>
      </div>
    );
  }

  renderViewToggle() {
    return (
      <div
        className='btn-group p-1'
        role='group'
      >
        <button
          type='button'
          className={`btn btn-sm ${this.state.view === 'team' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => this.setState({ view: 'team' })}
        >
          My team
        </button>
        <button
          type='button'
          className={`btn btn-sm ${this.state.view === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => this.setState({ view: 'grid' })}
        >
          Team grid
        </button>
        <button
          type='button'
          className={`btn btn-sm ${this.state.view === 'stats' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => this.setState({ view: 'stats' })}
        >
          Statistics
        </button>
        <button
          type='button'
          className={`btn btn-sm ${this.state.view === 'coverage' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => this.setState({ view: 'coverage' })}
        >
          Coverage
        </button>
      </div>
    );
  }

  renderIndividualView() {
    if (this.state.loading) {
      return <Loading />;
    }
    return (
      <>
        {this.renderMonthSelector()}
        {this.renderLegend()}
        {/* While refreshing, the calendar stays visible but dims and ignores clicks (the data is stale). */}
        <div className={this.state.refreshing ? 'ap-refreshing' : ''}>
          <AvailabilityPlannerTable
            users={this.state.users}
            year={this.state.year}
            month={this.state.month}
            onUserClick={(user) => this.populate(user.userId)}
            onCellClick={(user, startDay, endDay) => this.openDayPanel(user, startDay, endDay)}
            onQuickCreate={(user, dayNumber) => this.quickCreate(user, dayNumber)}
            onDayContextMenu={(e, user, dayNumber, hasEntry) => this.openDayContextMenu(e, user, dayNumber, hasEntry)}
          />
        </div>
        {this.state.window}
        {this.renderContextMenu()}
      </>
    );
  }

  renderContextMenuItem(kind, code, meta) {
    const menu = this.state.contextMenu;
    return (
      <button
        key={code}
        type='button'
        className='ap-ctx-item'
        onClick={() => this.quickSetDay(menu.user, menu.dayNumber, kind, code)}
      >
        <span className={`ap-legend-swatch ${meta.className}`}>
          <i className={`fa-solid ${meta.icon}`} />
        </span>
        {meta.label}
      </button>
    );
  }

  // Right-click quick menu: full-day work locations, full-day absence types, and Delete. Offered only on
  // editable, non-BambooHR-locked cells (the table gates the handler); clicking outside or Escape closes it.
  renderContextMenu() {
    const menu = this.state.contextMenu;
    if (!menu) {
      return null;
    }
    return (
      <div
        className='ap-ctx-overlay'
        onClick={this.closeContextMenu}
        onContextMenu={(e) => {
          e.preventDefault();
          this.closeContextMenu();
        }}
      >
        <div
          className='ap-ctx-menu'
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {Object.entries(LOCATION).map(([code, meta]) => this.renderContextMenuItem('work', code, meta))}
          <div className='ap-ctx-sep' />
          {Object.entries(ABSENCE).map(([code, meta]) => this.renderContextMenuItem('absence', code, meta))}
          <div className='ap-ctx-sep' />
          <button
            type='button'
            className='ap-ctx-item ap-ctx-delete'
            disabled={!menu.hasEntry}
            title={menu.hasEntry ? '' : 'This day has no entry to delete'}
            onClick={() => this.quickDeleteDay(menu.user, menu.dayNumber)}
          >
            <span className='ap-ctx-del-ico'>
              <i className='fa-solid fa-trash' />
            </span>
            Delete entry
          </button>
        </div>
      </div>
    );
  }

  renderCurrentView() {
    if (this.state.view === 'grid') {
      return <AvailabilityPlannerTeamGrid />;
    }
    if (this.state.view === 'stats') {
      return <AvailabilityPlannerStats />;
    }
    if (this.state.view === 'coverage') {
      return <AvailabilityPlannerCoverage />;
    }
    return this.renderIndividualView();
  }

  render() {
    return (
      // bravo-ds opts the page into the redesign scope; avplan-page maps the planner chrome (month label,
      // icons, legend, filters, footnotes) onto the theme tokens so all four views stay readable in dark
      // mode. The calendar/stat tables themselves stay white cards in both themes. NOTE: the class must NOT
      // be 'ap-page' — that's the redesign's Absence Planner class, whose rule hides the shell title band.
      <div className='contentwrapper bravo-ds avplan-page'>
        {this.renderViewToggle()}
        {this.renderCurrentView()}
      </div>
    );
  }
}
